import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, Mail, Phone, MapPin, User, FileText, Download, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { openLetterheadWindow } from '../utils/brandedPrint';
import CustomerReminderDialog from '../components/customers/CustomerReminderDialog';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const d10 = (v) => (v || '').slice(0, 10) || '—';
const esc = (v) => String(v ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const SECTIONS = [
  { key: 'sales_orders', title: 'Sales Orders', num: 'so_number', path: '/orders/sales', cols: [['Date', r => d10(r.order_date)], ['Total', r => money(r.total)], ['Balance', r => money(r.balance_due)]] },
  { key: 'production_orders', title: 'Production Orders', num: 'po_number', path: '/orders/production', cols: [['Date', r => d10(r.order_date || r.created_at)], ['Due', r => d10(r.due_date)]] },
  { key: 'packing_slips', title: 'Packing Slips', num: 'ps_number', path: '/orders/packing', cols: [['Ship Date', r => d10(r.ship_date)], ['Carrier', r => r.carrier || '—'], ['Tracking', r => r.tracking_number || '—']] },
  { key: 'bills_of_lading', title: 'Bills of Lading', num: 'bol_number', path: '/orders/bol', cols: [['Ship Date', r => d10(r.ship_date)], ['Carrier', r => r.carrier || '—']] },
  { key: 'repair_orders', title: 'Lyphline Repair Orders', num: 'ro_number', path: '/orders/repairs', cols: [['Intake', r => d10(r.intake_date)], ['Machines', r => (r.machines?.length ? r.machines.map(m => `${m.serial_number || '?'} (${m.status || 'Received'})`).join(', ') : (r.product_serial || r.product_name || '—'))], ['Total', r => money(r.total_cost)]] },
  { key: 'warranty_tickets', title: 'Warranty / RMAs', num: 'ticket_number', path: null, cols: [['Opened', r => d10(r.opened_at)], ['Serial', r => r.serial_number || '—'], ['Issue', r => (r.description || '').slice(0, 70)], ['Source', r => (r.source === 'external' ? `External${r.external_rma_number ? ` · ${r.external_rma_number}` : ''}` : 'In-app')]] },
];

const Stat = ({ label, value, testId }) => (
  <div className="border border-slate-200 rounded-sm px-4 py-3 bg-white" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-xl font-bold mono mt-0.5">{value}</div>
  </div>
);

const Section = ({ s, rows }) => (
  <Card className="industrial-card" data-testid={`history-section-${s.key}`}>
    <CardHeader className="border-b border-slate-100 pb-2"><CardTitle className="text-sm">{s.title} ({rows.length})</CardTitle></CardHeader>
    <CardContent className="p-0">
      {!rows.length ? <p className="text-xs text-slate-400 px-4 py-3">None yet.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left"><th className="px-4 py-2">#</th>{s.cols.map(([h]) => <th key={h} className="px-4 py-2">{h}</th>)}<th className="px-4 py-2">Status</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`history-row-${r.id}`}>
                <td className="px-4 py-2 mono font-semibold">{s.path ? <Link to={`${s.path}/${r.id}`} className="text-orange-700 hover:underline">{r[s.num]}</Link> : <Link to="/warranty" className="text-orange-700 hover:underline">{r[s.num]}</Link>}</td>
                {s.cols.map(([h, f]) => <td key={h} className="px-4 py-2 text-slate-700">{f(r)}</td>)}
                <td className="px-4 py-2"><span className="text-[10px] uppercase bg-slate-100 px-1.5 py-0.5 rounded-sm">{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardContent>
  </Card>
);

export default function CustomerHistoryPage() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [remOpen, setRemOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, rm] = await Promise.all([api().get(`/customers/${id}/history`), api().get(`/customers/${id}/reminders`).catch(() => ({ data: [] }))]);
      setData(r.data); setReminders(rm.data || []);
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load customer history'); }
  }, [api, id]);
  useEffect(() => { load(); }, [load]);

  const print = async () => {
    const c = data.customer; const t = data.totals;
    const sec = SECTIONS.map(s => {
      const rows = data[s.key] || [];
      return `<h2>${s.title} (${rows.length})</h2>${rows.length ? `<table><thead><tr><th>#</th>${s.cols.map(([h]) => `<th>${h}</th>`).join('')}<th>Status</th></tr></thead><tbody>${rows.map(r => `<tr><td class="mono">${esc(r[s.num])}</td>${s.cols.map(([, f]) => `<td>${esc(f(r))}</td>`).join('')}<td>${esc(r.status)}</td></tr>`).join('')}</tbody></table>` : '<p class="muted">None</p>'}`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Customer History · ${esc(c.customer_name)}</title><style>
      body{font-family:Arial,sans-serif;color:#0f172a;font-size:11px;margin:24px} h2{font-size:13px;margin:16px 0 6px;border-bottom:1px solid #cbd5e1;padding-bottom:3px}
      table{width:100%;border-collapse:collapse} th{font-size:9px;text-transform:uppercase;color:#64748b;text-align:left;padding:4px} td{padding:4px;border-top:1px solid #f1f5f9}
      .mono{font-family:ui-monospace,Menlo,Consolas,monospace} .muted{color:#94a3b8} .stats{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin:10px 0}
      .stat{border:1px solid #e2e8f0;padding:6px 8px} .stat b{display:block;font-size:14px} .stat span{font-size:8px;text-transform:uppercase;color:#64748b}
    </style></head><body><!--LETTERHEAD-->
      <div><strong style="font-size:15px">${esc(c.customer_name)}</strong><div class="muted">${[c.contact_person, c.email, c.phone].filter(Boolean).map(esc).join(' · ')}</div><div style="white-space:pre-line">${esc(c.billing_address || '')}</div></div>
      <div class="stats"><div class="stat"><span>Lifetime Sales</span><b>${money(t.lifetime_sales)}</b></div><div class="stat"><span>Open Orders</span><b>${t.open_orders}</b></div><div class="stat"><span>Open Repairs</span><b>${t.open_repairs}</b></div><div class="stat"><span>Warranty Claims</span><b>${t.warranty_claims}</b></div><div class="stat"><span>Last Order</span><b>${t.last_order_date || '—'}</b></div></div>
      ${sec}</body></html>`;
    const w = await openLetterheadWindow(html, { title: 'Customer History', subtitle: c.customer_name });
    if (!w) toast.error('Pop-ups blocked. Allow pop-ups to print.');
    else setTimeout(() => { try { w.print(); } catch (_) { /* ignore */ } }, 400);
  };

  const openStatement = async () => {
    const w = window.open('', '_blank', 'width=1000,height=1200');
    if (!w) { toast.error('Pop-ups blocked. Allow pop-ups to print.'); return; }
    w.document.write('Loading statement…');
    try {
      const r = await api().get(`/customers/${id}/statement.html`, { responseType: 'text' });
      w.document.open(); w.document.write(r.data); w.document.close();
      setTimeout(() => { try { w.focus(); w.print(); } catch (_) { /* ignore */ } }, 400);
    } catch { w.close(); toast.error('Statement failed to load'); }
  };

  const downloadStatement = async () => {
    try {
      const r = await api().get(`/customers/${id}/statement.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `Statement-${(data.customer.customer_name || 'customer').replace(/[^A-Za-z0-9]+/g, '-')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Statement PDF downloaded');
    } catch { toast.error('Download failed'); }
  };

  if (!data) return <div className="p-8 text-slate-400" data-testid="customer-history-loading">Loading…</div>;
  const c = data.customer; const t = data.totals;
  return (
    <div className="space-y-4" data-testid="customer-history-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/customers')} data-testid="customer-history-back"><ArrowLeft className="w-4 h-4 mr-1" /> Customers</Button>
          <div>
            <h1 className="text-2xl font-bold" data-testid="customer-history-name">{c.customer_name}</h1>
            <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-4 gap-y-1" data-testid="customer-history-contact">
              {c.contact_person && <span className="flex items-center gap-1"><User className="w-3 h-3" />{c.contact_person}</span>}
              {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
              {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
              {c.billing_address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.billing_address.replace(/\n/g, ', ')}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setRemOpen(true)} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="customer-send-reminder-btn"><BellRing className="w-3.5 h-3.5 mr-1" /> Send Reminder</Button>
          <Button size="sm" onClick={openStatement} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="customer-statement-btn"><FileText className="w-3.5 h-3.5 mr-1" /> Statement</Button>
          <Button variant="outline" size="sm" onClick={downloadStatement} className="rounded-sm" data-testid="customer-statement-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" /> Statement PDF</Button>
          <Button variant="outline" size="sm" onClick={print} className="rounded-sm" data-testid="customer-history-print-btn"><Printer className="w-3.5 h-3.5 mr-1" /> Print History</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Lifetime Sales" value={money(t.lifetime_sales)} testId="customer-stat-sales" />
        <Stat label="Open Orders" value={t.open_orders} testId="customer-stat-open-orders" />
        <Stat label="Open Repairs" value={t.open_repairs} testId="customer-stat-open-repairs" />
        <Stat label="Warranty Claims" value={t.warranty_claims} testId="customer-stat-warranty" />
        <Stat label="Last Order" value={t.last_order_date || '—'} testId="customer-stat-last-order" />
      </div>
      {SECTIONS.map(s => <Section key={s.key} s={s} rows={data[s.key] || []} />)}
      <Card className="industrial-card" data-testid="customer-reminders-log">
        <CardHeader className="border-b border-slate-100 pb-2"><CardTitle className="text-sm">Reminders Sent ({reminders.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!reminders.length ? <p className="text-xs text-slate-400 px-4 py-3">No reminders sent yet.</p> : reminders.map(rm => (
            <div key={rm.id} className="flex items-center justify-between text-sm px-4 py-2 border-t border-slate-100" data-testid={`customer-reminder-${rm.id}`}>
              <span><strong className="capitalize">{rm.tone === 'final' ? 'Final notice' : rm.tone}</strong> · {new Date(rm.sent_at).toLocaleString()} · by {rm.sent_by_name} · to {rm.to.join(', ')}</span>
              <span className="flex items-center gap-2">
                <span className="mono">{money(rm.total_overdue)} overdue</span>
                <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded-sm ${rm.status === 'sent' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} title={rm.detail || ''}>{rm.status === 'sent' ? 'Sent' : 'Not delivered'}</span>
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
      <CustomerReminderDialog api={api} customerId={id} open={remOpen} onOpenChange={setRemOpen} onSent={(rec) => setReminders(prev => [rec, ...prev])} />
    </div>
  );
}
